// useDriverTracking.js
//
// Watches the device's GPS while a route is IN_PROGRESS and POSTs throttled
// pings to nf-api. Throttle: skip a candidate point unless at least
// THROTTLE_MIN_INTERVAL_MS have elapsed since the last successful POST AND
// the candidate is at least THROTTLE_MIN_DISTANCE_METERS from the last POST.
//
// Usage:
//   const { startTracking, stopTracking, isTracking } = useDriverTracking();
//   watch(() => currentRoute.value?.status, (status, prev) => {
//     if (status === "IN_PROGRESS" && prev !== "IN_PROGRESS") {
//       startTracking(currentRoute.value.route_id);
//     } else if (status !== "IN_PROGRESS" && prev === "IN_PROGRESS") {
//       stopTracking();
//     }
//   });

import { onUnmounted, ref } from "vue";
import { useStore } from "vuex";
import { authHeaders } from "@/composables/apiAuth";

const THROTTLE_MIN_INTERVAL_MS = 30 * 1000;   // 30 s
const THROTTLE_MIN_DISTANCE_METERS = 50;      // 50 m

// Haversine distance in meters between two {lat,lng} points.
function distanceMeters(a, b) {
  const R = 6371000; // Earth radius in meters
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function useDriverTracking() {
  const store = useStore();
  const isTracking = ref(false);
  let watchId = null;
  let activeRouteId = null;
  let lastPostedAt = 0;
  let lastPostedPoint = null;
  let lastResetForRouteId = null;

  const postPing = async (point) => {
    try {
      const res = await fetch(`${store.state.api}recordDriverPing`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          routeId: activeRouteId,
          lat: point.lat,
          lng: point.lng,
          accuracy: point.accuracy ?? null,
          speed: point.speed ?? null,
          heading: point.heading ?? null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      // If the server rejects because the route is no longer in progress,
      // stop tracking. Saves a stale-route loop after the driver hits "complete".
      if (body.accepted === false && body.reason === "route not in progress") {
        stopTracking();
      }
      return body;
    } catch (err) {
      // Network / offline. Quietly drop — accept gaps in trail per spec.
      console.warn("recordDriverPing failed:", err?.message || err);
    }
  };

  const onPosition = (pos) => {
    const candidate = {
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
      speed: pos.coords.speed,
      heading: pos.coords.heading,
    };

    const now = Date.now();
    if (now - lastPostedAt < THROTTLE_MIN_INTERVAL_MS) return;
    if (
      lastPostedPoint &&
      distanceMeters(lastPostedPoint, candidate) < THROTTLE_MIN_DISTANCE_METERS
    ) {
      return;
    }

    lastPostedAt = now;
    lastPostedPoint = candidate;
    postPing(candidate);
  };

  const onError = (err) => {
    // Permission denied / position unavailable / timeout. Log once; the
    // browser will keep retrying internally based on the watch options.
    console.warn("watchPosition error:", err?.message || err);
  };

  const startTracking = (routeId) => {
    if (isTracking.value || !routeId) return;
    if (!("geolocation" in navigator)) {
      console.warn("Geolocation not supported");
      return;
    }
    // Reset throttle only on a genuine route switch. A start→stop→start
    // flap on the same route (e.g. status reactive blip during a refetch)
    // should preserve the existing 30s/50m window so we don't burst pings.
    if (routeId !== lastResetForRouteId) {
      lastPostedAt = 0;
      lastPostedPoint = null;
      lastResetForRouteId = routeId;
    }
    activeRouteId = routeId;
    watchId = navigator.geolocation.watchPosition(onPosition, onError, {
      enableHighAccuracy: false,
      maximumAge: 30000,
      timeout: 60000,
    });
    isTracking.value = true;
  };

  const stopTracking = () => {
    if (!isTracking.value) return;
    if (watchId !== null) navigator.geolocation.clearWatch(watchId);
    watchId = null;
    activeRouteId = null;
    isTracking.value = false;
  };

  // Auto-cleanup: when the host component unmounts, stop the GPS watch so it
  // doesn't keep firing into a destroyed closure.
  onUnmounted(() => {
    stopTracking();
  });

  return { startTracking, stopTracking, isTracking };
}
